import clsx from 'clsx';
import { MdLayers } from 'react-icons/md';

import { getApiErrorMessage } from '~api/client';
import { OverlayWidget } from '~components';
import { Button } from '~components/ui/button';
import {
  ArShapesLayer,
  BlockLabelsLayer,
  BlocksLayer,
  DrillDomainsLayer,
  DrilledDrillHolesLayer,
  LayerControlPopover,
  LayerItem,
  LayerMask,
  NeighbouringDrillHolesLayer,
  Polygon,
  SvgCanvas,
  SvgGrid,
  SvgScaleLegend,
} from '~libs';
import { useBlastDesignContext } from '../context';
import { formatDrilledHoleTooltip, formatNeighbouringDrillHoleTooltip } from '../utils';

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

/**
 * Renders the LayerControlPopover with all layer toggle items for the blast design
 * context canvas. Extracted to keep BlastDesignContextCanvas within complexity limits.
 */
const BlastDesignContextLayerControls = () => {
  const {
    haveBlocks,
    blocksQuery,
    haveDrillDomains,
    drillDomainsQuery,
    haveDrilledHoles,
    drilledDataQuery,
    haveNeighbouringDrillHoles,
    neighbouringDrillHolesQuery,
    haveArShapes,
    arShapesQuery,
    layers,
  } = useBlastDesignContext();

  return (
    <LayerControlPopover
      trigger={
        <Button
          icon
          variant="ghost"
          aria-label="Open layers panel"
          className={clsx(
            'min-h-10 rounded-md border border-border/20 bg-background p-2 text-primary shadow-md',
            'data-[state=open]:bg-primary data-[state=open]:text-primary-foreground',
            'data-[state=open]:hover:bg-primary data-[state=open]:dark:hover:text-primary',
          )}
        >
          <MdLayers className="size-5" />
        </Button>
      }
      title={
        <span className="flex items-center gap-2">
          <MdLayers className="size-4" />
          Map Layers
        </span>
      }
    >
      <LayerItem label="Blast Pattern" layerVisibility={layers.blast} />
      <LayerItem
        label="Block Model"
        layerVisibility={layers.blocks}
        isLoading={blocksQuery.isLoading}
        isError={blocksQuery.isError || (!blocksQuery.isLoading && !haveBlocks)}
        errorMessage={
          blocksQuery.isError ? getApiErrorMessage(blocksQuery.error) : 'No block models have been found for this blast'
        }
      />
      <LayerItem
        label="Drill domains"
        layerVisibility={layers.drillDomains}
        isLoading={drillDomainsQuery.isLoading}
        isError={drillDomainsQuery.isError || (!drillDomainsQuery.isLoading && !haveDrillDomains)}
        errorMessage={
          drillDomainsQuery.isError
            ? getApiErrorMessage(drillDomainsQuery.error)
            : 'No drill domains have been found for this blast'
        }
      />
      <LayerItem
        label="Drilled Holes"
        layerVisibility={layers.holes}
        isLoading={drilledDataQuery.isLoading}
        isError={drilledDataQuery.isError || (!drilledDataQuery.isLoading && !haveDrilledHoles)}
        errorMessage={
          drilledDataQuery.isError
            ? getApiErrorMessage(drilledDataQuery.error)
            : 'No drilled holes have been found for this blast'
        }
      />
      <LayerItem
        label="Neighbouring Drilled Holes"
        layerVisibility={layers.neighbouringDrillHoles}
        isLoading={neighbouringDrillHolesQuery.isLoading}
        isError={
          neighbouringDrillHolesQuery.isError || (!neighbouringDrillHolesQuery.isLoading && !haveNeighbouringDrillHoles)
        }
        errorMessage={
          neighbouringDrillHolesQuery.isError
            ? getApiErrorMessage(neighbouringDrillHolesQuery.error)
            : 'No neighbouring drilled holes have been found for this blast'
        }
      />
      <LayerItem
        label="AR Permits"
        layerVisibility={layers.arShapes}
        isLoading={arShapesQuery.isLoading}
        isError={arShapesQuery.isError || (!arShapesQuery.isLoading && !haveArShapes)}
        errorMessage={
          arShapesQuery.isError
            ? getApiErrorMessage(arShapesQuery.error)
            : 'No AR permits have been found for this blast'
        }
      />
    </LayerControlPopover>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Main canvas component for the Blast Design Context page.
 * Renders the interactive SVG canvas with multiple layers for blast polygon, block model, and drilled holes overlays.
 * Layer visibility is controlled via the LayerControlPopover, and data is sourced from the BlastDesignContext.
 *
 * @example
 * ```tsx
 * <BlastDesignContextProvider>
 *   <BlastDesignContextCanvas />
 * </BlastDesignContextProvider>
 * ```
 */
const BlastDesignContextCanvas = () => {
  const {
    blast,
    blocks,
    drilledDataQuery,
    drillDomainsQuery,
    arShapesQuery,
    layers,
    selectedBlockId,
    blockColourScale,
    showBlockLabels,
    selectedHoleId,
    drilledHoleColourScale,
    selectedDrillHoleId,
    neighbouringHoleColourScale,
    filteredNeighbouringDrillHoles,
    selectedArShapeId,
    plannedOrActual,
    collarOrToe,
    handleBlockClick,
    handleHoleClick,
    handleDrillHoleClick,
    handleArShapeClick,
  } = useBlastDesignContext();

  return (
    <section className="relative flex flex-1 items-center justify-center bg-background">
      <SvgCanvas
        title={`Blast design context visualization for ${blast.name ?? 'blast'}`}
        description="Interactive map showing blast design context with drill domains, block model, and drilled holes"
        boundedByGeometries={[blast.polygon]}
        overlay={
          <>
            <SvgScaleLegend />
            <OverlayWidget>
              <BlastDesignContextLayerControls />
            </OverlayWidget>
          </>
        }
      >
        {/* Layer 0: Adaptive grid */}
        <SvgGrid />

        {/* Layer 1: Blast polygon background */}
        <LayerMask layerVisibility={layers.blast}>
          <Polygon geometry={blast.polygon} />
        </LayerMask>

        {/* Layer 2: AR Permits */}
        <LayerMask layerVisibility={layers.arShapes}>
          {arShapesQuery.data && (
            <ArShapesLayer
              arShapes={arShapesQuery.data}
              onShapeClick={handleArShapeClick}
              selectedShapeId={selectedArShapeId}
            />
          )}
        </LayerMask>

        {/* Layer 3: Drill domains */}
        <LayerMask layerVisibility={layers.drillDomains}>
          {drillDomainsQuery.data && <DrillDomainsLayer domains={drillDomainsQuery.data} />}
        </LayerMask>

        {/* Layer 4: Block model */}
        {blocks && blocks.length > 0 && (
          <LayerMask layerVisibility={layers.blocks}>
            <BlocksLayer
              blocks={blocks}
              selectedBlockId={selectedBlockId}
              colourScale={blockColourScale}
              onBlockClick={handleBlockClick}
            />
            {showBlockLabels && <BlockLabelsLayer blocks={blocks} colourScale={blockColourScale} />}
          </LayerMask>
        )}

        {/* Layer 5: Drilled holes */}
        <LayerMask layerVisibility={layers.holes}>
          {drilledDataQuery.data?.drilled_data && drilledDataQuery.data.drilled_data.length > 0 && (
            <DrilledDrillHolesLayer
              drillHoles={drilledDataQuery.data.drilled_data}
              selectedHoleId={selectedHoleId}
              colourScale={drilledHoleColourScale}
              plannedOrActualCoordinates={plannedOrActual}
              onHoleSelect={handleHoleClick}
              titleFormatter={formatDrilledHoleTooltip}
            />
          )}
        </LayerMask>

        {/* Layer 6: Neighbouring drilled holes */}
        <LayerMask layerVisibility={layers.neighbouringDrillHoles}>
          {filteredNeighbouringDrillHoles && filteredNeighbouringDrillHoles.length > 0 && (
            <NeighbouringDrillHolesLayer
              drillHoles={filteredNeighbouringDrillHoles}
              selectedHoleId={selectedDrillHoleId}
              colourScale={neighbouringHoleColourScale}
              collarOrToe={collarOrToe}
              onHoleSelect={handleDrillHoleClick}
              titleFormatter={formatNeighbouringDrillHoleTooltip}
            />
          )}
        </LayerMask>
      </SvgCanvas>
    </section>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlastDesignContextCanvas };
